import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (d) => {
  if (!d) return "The archive is momentarily unreachable. Try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(d?.msg || d);
};

const SeekerForm = ({ onJoined }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || password.length < 6) {
      toast.error("The stories require a name, an address, and a key of six letters or more.");
      return;
    }
    setState("loading");
    try {
      await axios.post(
        `${API}/seekers`,
        { name: name.trim(), email: email.trim(), password },
        { withCredentials: true }
      );
      setState("done");
      if (onJoined) onJoined();
    } catch (err) {
      setState("idle");
      toast.error(formatDetail(err?.response?.data?.detail));
    }
  };

  if (state === "done") {
    return (
      <motion.div
        data-testid="seeker-success"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col items-center gap-4 py-6 text-center"
      >
        <Eye className="text-[#e8c95a]" size={30} strokeWidth={1.2} />
        <p className="font-serif-engraved text-2xl text-[#e8c95a]">
          The stories have noted your name.
        </p>
        <p className="max-w-sm text-sm font-light text-[#fdfbf7]/55">
          Welcome, seeker. A scroll is on its way to your address, and the door
          of the Celestial Society now stands open to you.
        </p>
        <Link
          to="/society"
          data-testid="enter-society-button"
          className="group mt-3 inline-flex items-center gap-3 border border-[#c9a227]/50 px-7 py-3 text-[11px] uppercase tracking-[0.3em] text-[#e8c95a] transition-colors duration-300 hover:bg-[#c9a227]/10"
        >
          Enter the Society
          <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-2" />
        </Link>
      </motion.div>
    );
  }

  return (
    <form data-testid="seeker-form" onSubmit={submit} className="mx-auto flex w-full max-w-md flex-col gap-4">
      <input
        data-testid="seeker-name-input"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name, as it should be remembered"
        className="border border-[#c9a227]/25 bg-transparent px-5 py-3.5 text-center text-sm font-light tracking-wide text-[#fdfbf7] placeholder:text-[#fdfbf7]/30 focus:border-[#c9a227]/70 focus:outline-none transition-colors duration-300"
      />
      <input
        data-testid="seeker-email-input"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your correspondence address"
        className="border border-[#c9a227]/25 bg-transparent px-5 py-3.5 text-center text-sm font-light tracking-wide text-[#fdfbf7] placeholder:text-[#fdfbf7]/30 focus:border-[#c9a227]/70 focus:outline-none transition-colors duration-300"
      />
      <input
        data-testid="seeker-password-input"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Choose your key — six letters or more"
        className="border border-[#c9a227]/25 bg-transparent px-5 py-3.5 text-center text-sm font-light tracking-wide text-[#fdfbf7] placeholder:text-[#fdfbf7]/30 focus:border-[#c9a227]/70 focus:outline-none transition-colors duration-300"
      />
      <button
        data-testid="seeker-submit-button"
        type="submit"
        disabled={state === "loading"}
        className="group relative overflow-hidden border border-[#c9a227]/50 py-3.5 text-[12px] uppercase tracking-[0.32em] text-[#e8c95a] transition-colors duration-500 hover:text-[#050a06] disabled:opacity-50"
      >
        <span className="absolute inset-0 -translate-x-full bg-[#c9a227] transition-transform duration-500 ease-out group-hover:translate-x-0" />
        <span className="relative">{state === "loading" ? "Inscribing…" : "Take the Oath"}</span>
      </button>
    </form>
  );
};

export default SeekerForm;
